import React, {useContext, useState, useEffect} from 'react';
import {
  AppBar,
  Button,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import axios from 'axios';
import {FirebaseContext, SheetContext} from '../../context';
import {RoutingContext} from '../../routing';
import {dateFormat, sheet} from '../../utils';

import './GetStarted.scss';

function StepOne({loading, setLoading, nextStep}) {
  const firebase = useContext(FirebaseContext);
  const {navigateToLogin} = useContext(RoutingContext);
  const {saveProfile, profile, accessTokenGoogle, signOut} = firebase;
  const [hasAuthError, setHasAuthError] = useState(!accessTokenGoogle);
  let axiosOptions = {
    headers: {
      'Authorization': `Bearer ${accessTokenGoogle}`
    }
  };

  /**
   * Create a new spreadsheet with the sheets and headers from the default config
   * @returns {Promise<void>}
   */
  async function createSheet() {
    let response;
    try {
      response = await axios.post(
        'https://sheets.googleapis.com/v4/spreadsheets',
        sheet.defaultSpreadsheetConfig,
        axiosOptions
      );
      return response.data;
    } catch (ex) {
      return ex.response.data;
    }
  }

  /**
   * Setup profile for the given spreadsheet
   * @param spreadsheet
   * @returns {Promise<void>}
   */
  async function createProfile(spreadsheet) {
    const { spreadsheetId, sheets} = spreadsheet;
    if (!spreadsheetId) {
      throw new Error('SpreadsheetId not found');
    }

    const profile = {
      spreadsheetId,
    };

    const sheetsByTitle = sheets.reduce((result, sheet) => {
      const { properties } = sheet;
      if (properties) {
        result[properties.title] = properties;
      }
      return result;
    }, {});

    // get sheets from spreadsheet
    if (sheetsByTitle['Expenses']) {
      profile.sheetIdExpenses = sheetsByTitle['Expenses'].sheetId;
    }
    if (sheetsByTitle['Exchanges']) {
      profile.sheetIdExchanges = sheetsByTitle['Exchanges'].sheetId;
    }
    if (sheetsByTitle['Categories']) {
      profile.sheetIdCategories = sheetsByTitle['Categories'].sheetId;
    }

    return saveProfile(profile);
  }

  /**
   * Share the given spreadsheet with service account
   * @param spreadsheetId
   * @returns {Promise<void>}
   */
  async function shareSheet(spreadsheetId) {
    const url = `https://www.googleapis.com/drive/v3/files/${spreadsheetId}/permissions`;
    return axios.post(url, {
      role: 'writer',
      type: 'user',
      emailAddress: import.meta.env.VITE_GOOGLE_CLOUD_CLIENT_EMAIL,
    }, axiosOptions);
  }

  async function initSheet() {
    if (profile && profile.spreadsheetId) {
      return nextStep();
    }
    if (accessTokenGoogle) {
      setLoading(true);
      const spreadsheet = await createSheet();
      if (spreadsheet.error) {
        console.error(spreadsheet.error);
        const {code} = spreadsheet.error;
        if (code >= 400 && code < 500) {
          setHasAuthError(true);
          setLoading(false);
          return;
        }
      }
      const { spreadsheetId } = spreadsheet;


      // Share file with service account
      await shareSheet(spreadsheetId);

      // Store the spreadsheet and sheet IDs in user profile
      await createProfile(spreadsheet);

      setLoading(false);
      nextStep();
    } else {
      console.error('No Google access token available');
      setHasAuthError(true);
    }
  }
  const logout = () => {
    signOut();
    navigateToLogin();
  }

  if (hasAuthError) {
    return <div>

      <Typography variant="h5" component="p">Oops! 😬</Typography>

      <Typography component="p">It looks like there is a permissions problem accessing your Google account.</Typography>
      <Typography component="p">Please click below to logout and login again, making sure you give permission to edit and create Google Drive files when prompted.</Typography>

      <Button
        onClick={logout}
        variant="contained"
      >Logout</Button>
    </div>;
  }
  return <div>
    <Typography component="p">The first step is to create a new Google Sheet.</Typography>

    <Typography component="p">Click the button below to create a Google Sheet in your account. We will name it "LebSpend Expenses" so you can always access it later.</Typography>

    <LoadingButton
      onClick={initSheet}
      loading={loading}
      variant="contained"
    >Create Sheet</LoadingButton>
    {loading ? <Typography variant="caption" display="block">This might take a few seconds.<br/>Please wait.</Typography> : null}
  </div>
}
function StepTwo({loading, setLoading, error, setError, nextStep}) {
  const {addExchange} = useContext(SheetContext);
  const [usd, setUsd] = useState('');
  const [rate, setRate] = useState('');

  useEffect(() => {
    setError(null);
  }, [usd, rate, setError]);
  async function submit() {
    if (!usd || usd <= 0) {
      setError('Please enter Dollars Exchanged');
      return;
    }
    if (rate < 2000) {
      setError('The exchange rate hasn\'t been that low in years');
      return;
    }

    setLoading(true);
    await addExchange({
      Date: dateFormat.format(new Date()),
      LBP: usd * rate,
      USD: usd,
    });
    setLoading(false);
    nextStep();
  }
  return <div>
    <Typography variant="h5" component="p">Congrats! 🚀</Typography>
    <Typography component="p">You're Google Sheet is created!</Typography>
    <Typography component="p">However, it's currently empty. 🙁</Typography>
    <Typography component="p">Let's add some data by recording your last exchange. Enter below how much dollars you last exchanged and what rate you exchanged at (or estimate your best guess).</Typography>

    <TextField
      required
      fullWidth
      id="usd"
      name="usd"
      type="number"
      label="Dollars Exchanged"
      value={usd}
      variant="filled"
      disabled={loading}
      error={!!error}
      onChange={(event) => setUsd(event.target.value)}
      InputProps={{
        startAdornment: <InputAdornment sx={{marginY:'-1rem'}} position="start">$</InputAdornment>,
      }}
    />

    <TextField
      required
      fullWidth
      id="rate"
      name="rate"
      type="number"
      label="Exchange Rate (e.g. 100,000)"
      value={rate}
      variant="filled"
      helperText={error}
      disabled={loading}
      error={!!error}
      onChange={(event) => setRate(event.target.value)}
      InputProps={{
        startAdornment: <InputAdornment sx={{marginY:'-1rem'}} position="start">LBP</InputAdornment>,
      }}
    />

    <LoadingButton
      loading={loading}
      onClick={submit}
      target="_blank"
      variant="contained"
      size="large"
      className="finish-btn"
    >Save Exchange</LoadingButton>
  </div>
}
function StepThree({loading, setLoading, error, setError, nextStep}) {
  const {addExpense, getExchanges, exchanges} = useContext(SheetContext);
  const [lbp, setLbp] = useState('');

  const submit = async () => {
    if (!lbp || lbp <= 0) {
      setError('Please enter LBP On-hand');
      return;
    }

    // go fetch the list of exchanges if we don't have it yet
    if (!exchanges || !exchanges.length) {
      await getExchanges();
    }

    let expenseAmount = lbp;
    if (exchanges && exchanges.length) {
      // there should be just one at this point
      // Subtract the total they have remaining from the exchange to get how much was spent
      expenseAmount = exchanges[0].lbp - lbp;
    }
    setLoading(true);
    await addExpense({
      Date: dateFormat.format(new Date()),
      Category: 'Other',
      Description: 'Initial balance adjustment',
      LBP: expenseAmount,
    });
    setLoading(false);
    nextStep();
  }
  return <div>
    <Typography variant="h5" component="p">You're almost ready!</Typography>

    <Typography component="p">To keep an accurate count of how much lira you have available, count how much you have on-hand currently and enter it below. We'll create a first expense to account for how much you've used since the last exchange.</Typography>

    <TextField
      required
      fullWidth
      id="lbp"
      name="lbp"
      label="LBP On-hand"
      value={lbp}
      type="number"
      variant="filled"
      helperText={error}
      disabled={loading}
      error={!!error}
      onChange={(event) => setLbp(event.target.value)}
      InputProps={{
        startAdornment: <InputAdornment sx={{marginY:'-1rem'}} position="start">LBP</InputAdornment>,
      }}
    />

    <LoadingButton
      loading={loading}
      onClick={submit}
      target="_blank"
      variant="contained"
      size="large"
      className="finish-btn"
    >Finish</LoadingButton>
  </div>
}
function GetStarted({maxStep: maxStepProp, step, loading: loadingProp, error: errorProp}) {
  const firebase = useContext(FirebaseContext);
  const {navigateToRoot} = useContext(RoutingContext);
  const {profile} = firebase;
  const [loading, setLoading] = useState(loadingProp || false);
  const [error, setError] = useState(errorProp);
  let initialStep = step;
  if (profile && profile.spreadsheetId) {
    initialStep = 1;
  }
  const [activeStep, setActiveStep] = useState(initialStep || 0);
  const [maxStep, setMaxStep] = useState(maxStepProp || initialStep || 0);

  useEffect(() => {
    setMaxStep((s) => Math.max(s, activeStep));
    if (activeStep > 2) {
      navigateToRoot();
    }
  }, [activeStep, setMaxStep, navigateToRoot]);

  const nextStep = () => setActiveStep((prev) => prev + 1);
  const prevStep = () => setActiveStep((prev) => prev - 1);

  let StepContent;
  switch (activeStep) {
    case 2:
      StepContent = StepThree;
      break;
    case 1:
      StepContent = StepTwo;
      break;
    default:
      StepContent = StepOne;
  }
  return (
    <div className="page page-get-started">
      <AppBar color="transparent" position="static">
        <Toolbar>
          <Stepper activeStep={activeStep} alternativeLabel>
            <Step>
              <StepLabel>Create Sheet</StepLabel>
            </Step>
            <Step>
              <StepLabel>Add Exchange</StepLabel>
            </Step>
            <Step>
              <StepLabel>Record Balance</StepLabel>
            </Step>
          </Stepper>
        </Toolbar>
      </AppBar>

      <div className="page-content">
        <StepContent nextStep={nextStep}
                     loading={loading}
                     setLoading={setLoading}
                     error={error}
                     setError={setError}
        />
      </div>
      <div className="stepper-bottom-nav">
        {activeStep > 0 ?
          <Button onClick={prevStep} className="prev">
            Previous
          </Button>
          : <Typography>&nbsp;</Typography> }
        {activeStep < maxStep ?
          <LoadingButton onClick={nextStep} loading={loading} className="next">
            Next
          </LoadingButton>
          : activeStep > 0 ?
            <LoadingButton onClick={nextStep} loading={loading} className="next">
              Skip
            </LoadingButton>
            : <Typography>&nbsp;</Typography>}
      </div>
    </div>
  );
}

export default GetStarted;
